import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { IconArrowLeft, IconDots, IconUserCheck } from '@tabler/icons';
import { formatDistanceToNow, parseJSON } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth';
import {
  useInternalAccount,
  useInternalAccountUsers,
  useActiveAssumedRoles,
  useAccountAssumedRoles,
  useEndAssumedRole,
  useInternalAccountEntitlements,
  useAddEntitlement,
  useRemoveEntitlement,
  useUpdateEntitlement,
  useInternalAccountDomains,
  useRemoveDomain,
} from '../../data/useInternalAdmin';
import type { InternalAccountUser } from '../../data/useInternalAdmin';
import { canPerform } from '../../utils/internalPermissions';
import AQDataGrid from '../AQDataGrid';
import ChangeAccountUserRoleDialog from './ChangeAccountUserRoleDialog';
import RemoveAccountUserDialog from './RemoveAccountUserDialog';
import AssumeRoleDialog from './AssumeRoleDialog';
import DeactivateAccountDialog from './DeactivateAccountDialog';
import AddEntitlementDialog from './AddEntitlementDialog';
import AddDomainDialog from './AddDomainDialog';

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  creator: 'Creator',
  'account-admin': 'Admin',
};

const ROLE_COLORS: Record<string, 'default' | 'primary' | 'warning'> = {
  viewer: 'default',
  creator: 'primary',
  'account-admin': 'warning',
};

const InternalAccountDetail: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const { user } = useAuth();
  const internalRole = user?.internal_role;
  const navigate = useNavigate();

  const accountQuery = useInternalAccount(accountId);
  const usersQuery = useInternalAccountUsers(accountId);
  const activeRolesQuery = useActiveAssumedRoles();
  const activeRoles = activeRolesQuery.data?.data || [];
  const accountRolesQuery = useAccountAssumedRoles(accountId);
  const accountAssumedRoles = accountRolesQuery.data?.data || [];
  const endRole = useEndAssumedRole();

  const entitlementsQuery = useInternalAccountEntitlements(accountId);
  const entitlementsList = entitlementsQuery.data?.data || [];
  const removeEntitlement = useRemoveEntitlement(accountId);
  const updateEntitlement = useUpdateEntitlement(accountId);

  const domainsQuery = useInternalAccountDomains(accountId);
  const domainsList = domainsQuery.data?.data || [];
  const removeDomain = useRemoveDomain(accountId);

  const [tab, setTab] = React.useState(0);
  const [assumeRoleOpen, setAssumeRoleOpen] = React.useState(false);
  const [addEntitlementOpen, setAddEntitlementOpen] = React.useState(false);
  const [addDomainOpen, setAddDomainOpen] = React.useState(false);
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);
  const [roleTarget, setRoleTarget] = React.useState<{
    userId: string;
    displayName: string;
    currentRole: string;
  } | null>(null);
  const [removeTarget, setRemoveTarget] = React.useState<{
    userId: string;
    displayName: string;
  } | null>(null);
  const [menuAnchor, setMenuAnchor] = React.useState<{
    el: HTMLElement;
    user: InternalAccountUser;
  } | null>(null);

  const account = accountQuery.data?.data;
  const isDeactivated = Boolean(account?.attributes.deactivated_at);
  const users = usersQuery.data?.data || [];

  const userRows = React.useMemo(
    () =>
      users.map((u) => ({
        id: u.id,
        display_name: u.attributes.display_name || '-',
        email: u.attributes.email,
        role: u.attributes.role,
        _raw: u,
      })),
    [users],
  );

  const colDefs = React.useMemo(
    () => [
      {
        field: 'display_name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        sortable: true,
        filterable: false,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 220,
        sortable: true,
        filterable: true,
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 140,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => {
          const clickable = canPerform(internalRole, 'update_account_user');
          return (
            <Chip
              label={ROLE_LABELS[params.value] || params.value}
              size="small"
              color={ROLE_COLORS[params.value] || 'default'}
              variant="outlined"
              onClick={
                clickable
                  ? () =>
                      setRoleTarget({
                        userId: params.row.id,
                        displayName: params.row.display_name,
                        currentRole: params.value,
                      })
                  : undefined
              }
              sx={clickable ? { cursor: 'pointer' } : undefined}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: (params: any) =>
          canPerform(internalRole, 'remove_account_user') ? (
            <IconButton
              size="small"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                setMenuAnchor({ el: e.currentTarget, user: params.row._raw })
              }
            >
              <IconDots size={16} />
            </IconButton>
          ) : null,
      },
    ],
    [internalRole],
  );

  const gridProps = React.useMemo(
    () => ({
      getEstimatedRowHeight: () => 52,
      getRowHeight: () => 'auto' as const,
      sortingMode: 'client' as const,
      filterMode: 'client' as const,
      paginationMode: 'client' as const,
      disableColumnMenu: true,
      disableRowSelectionOnClick: true,
    }),
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Back link */}
      <Link
        component="button"
        variant="body2"
        onClick={() => navigate('/internal/accounts')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 2,
          textDecoration: 'none',
        }}
      >
        <IconArrowLeft size={16} />
        Back to Accounts
      </Link>

      {/* Account header */}
      {account && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5">
                {account.attributes.title || account.attributes.name}
              </Typography>
              <Chip
                label={
                  account.attributes.is_internal_private
                    ? 'Internal'
                    : 'Customer'
                }
                size="small"
                color={
                  account.attributes.is_internal_private ? 'default' : 'primary'
                }
                variant="outlined"
              />
              {isDeactivated && (
                <Chip
                  label="Deactivated"
                  size="small"
                  color="error"
                  variant="filled"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canPerform(internalRole, 'deactivate_account') && (
                <Button
                  variant="outlined"
                  size="small"
                  color={isDeactivated ? 'primary' : 'error'}
                  onClick={() => setDeactivateOpen(true)}
                >
                  {isDeactivated ? 'Reactivate' : 'Deactivate'}
                </Button>
              )}
              {canPerform(internalRole, 'assume_role') && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<IconUserCheck size={16} />}
                  onClick={() => setAssumeRoleOpen(true)}
                  disabled={isDeactivated}
                >
                  Assume Role
                </Button>
              )}
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {account.attributes.name} &middot; Created{' '}
            {formatDistanceToNow(parseJSON(account.attributes.created_at), {
              addSuffix: true,
            })}
          </Typography>
          {isDeactivated && account.attributes.deactivated_at && (
            <Alert severity="error" sx={{ mt: 2 }}>
              This account was deactivated{' '}
              {formatDistanceToNow(
                parseJSON(account.attributes.deactivated_at),
                {
                  addSuffix: true,
                },
              )}
              . All user access is blocked.
            </Alert>
          )}
        </Box>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Users" />
          <Tab label="Assumed Roles" />
          <Tab label="Entitlements" />
          <Tab label="Domains" />
        </Tabs>
      </Box>

      {/* Users tab */}
      {tab === 0 && (
        <>
          <AQDataGrid
            id="internal-account-users"
            rows={userRows}
            colDefs={colDefs}
            loading={usersQuery.isLoading}
            gridProps={gridProps}
            customErrorElement={
              usersQuery.isError ? (
                <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
                  Failed to load users
                </Typography>
              ) : undefined
            }
            sx={{
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                py: '12px',
              },
            }}
          />
        </>
      )}

      {/* Assumed Roles tab */}
      {tab === 1 && (
        <AQDataGrid
          id="internal-account-assumed-roles"
          rows={accountAssumedRoles.map((r) => ({
            id: r.id,
            user_email:
              r.attributes.user_email || r.attributes.internal_user_id,
            assumed_role: r.attributes.assumed_role,
            reason: r.attributes.reason,
            started_at: r.attributes.started_at,
            ended_at: r.attributes.ended_at,
            expires_at: r.attributes.expires_at,
            _isActive: !r.attributes.ended_at,
          }))}
          colDefs={[
            {
              field: 'user_email',
              headerName: 'User',
              flex: 1,
              minWidth: 200,
              sortable: true,
              filterable: true,
            },
            {
              field: 'assumed_role',
              headerName: 'Role',
              width: 130,
              sortable: true,
              filterable: false,
              renderCell: (params: any) => (
                <Chip
                  label={ROLE_LABELS[params.value] || params.value}
                  size="small"
                  color={ROLE_COLORS[params.value] || 'default'}
                  variant="outlined"
                />
              ),
            },
            {
              field: 'reason',
              headerName: 'Reason',
              flex: 1,
              minWidth: 160,
              sortable: false,
              filterable: false,
            },
            {
              field: 'started_at',
              headerName: 'Started',
              width: 160,
              sortable: true,
              filterable: false,
              renderCell: (params: any) => (
                <Typography variant="body2" color="text.secondary">
                  {formatDistanceToNow(parseJSON(params.value), {
                    addSuffix: true,
                  })}
                </Typography>
              ),
            },
            {
              field: 'ended_at',
              headerName: 'Status',
              width: 140,
              sortable: true,
              filterable: false,
              renderCell: (params: any) =>
                params.value ? (
                  <Typography variant="body2" color="text.secondary">
                    {formatDistanceToNow(parseJSON(params.value), {
                      addSuffix: true,
                    })}
                  </Typography>
                ) : (
                  <Chip label="Active" size="small" color="success" />
                ),
            },
            {
              field: 'actions',
              headerName: '',
              width: 120,
              sortable: false,
              filterable: false,
              renderCell: (params: any) =>
                params.row._isActive &&
                canPerform(internalRole, 'end_assumed_role') ? (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => endRole.mutate(params.row.id)}
                    disabled={endRole.isPending}
                  >
                    Deactivate
                  </Button>
                ) : null,
            },
          ]}
          loading={accountRolesQuery.isLoading}
          gridProps={{
            rowHeight: 60,
            sortingMode: 'client' as const,
            filterMode: 'client' as const,
            paginationMode: 'client' as const,
            disableColumnMenu: true,
            disableRowSelectionOnClick: true,
          }}
          customErrorElement={
            accountRolesQuery.isError ? (
              <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
                Failed to load assumed roles
              </Typography>
            ) : undefined
          }
          sx={{
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      )}

      {/* Entitlements tab */}
      {tab === 2 && (
        <>
          {canPerform(internalRole, 'manage_entitlements') && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setAddEntitlementOpen(true)}
              >
                Add Entitlement
              </Button>
            </Box>
          )}
          <AQDataGrid
            id="internal-account-entitlements"
            rows={entitlementsList.map((e) => ({
              id: e.id,
              entitlement_type: e.attributes.entitlement_type,
              entitlement_key: e.attributes.entitlement_key,
              value: e.attributes.value,
              created_at: e.attributes.created_at,
              _raw: e,
            }))}
            colDefs={[
              {
                field: 'entitlement_type',
                headerName: 'Type',
                width: 140,
                sortable: true,
                filterable: true,
                renderCell: (params: any) => (
                  <Chip
                    label={params.value}
                    size="small"
                    color={
                      params.value === 'feature'
                        ? 'primary'
                        : params.value === 'limit'
                          ? 'warning'
                          : 'default'
                    }
                    variant="outlined"
                  />
                ),
              },
              {
                field: 'entitlement_key',
                headerName: 'Key',
                flex: 1,
                minWidth: 200,
                sortable: true,
                filterable: true,
              },
              {
                field: 'value',
                headerName: 'Value',
                width: 160,
                sortable: false,
                filterable: false,
                renderCell: (params: any) => (
                  <Typography variant="body2">
                    {params.value !== null ? params.value : 'Granted'}
                  </Typography>
                ),
              },
              {
                field: 'actions',
                headerName: '',
                width: 100,
                sortable: false,
                filterable: false,
                renderCell: (params: any) =>
                  canPerform(internalRole, 'manage_entitlements') ? (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => removeEntitlement.mutate(params.row.id)}
                      disabled={removeEntitlement.isPending}
                    >
                      Remove
                    </Button>
                  ) : null,
              },
            ]}
            loading={entitlementsQuery.isLoading}
            gridProps={{
              rowHeight: 52,
              sortingMode: 'client' as const,
              filterMode: 'client' as const,
              paginationMode: 'client' as const,
              disableColumnMenu: true,
              disableRowSelectionOnClick: true,
            }}
            customErrorElement={
              entitlementsQuery.isError ? (
                <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
                  Failed to load entitlements
                </Typography>
              ) : undefined
            }
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </>
      )}

      {/* Domains tab */}
      {tab === 3 && (
        <>
          {canPerform(internalRole, 'manage_entitlements') && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setAddDomainOpen(true)}
              >
                Add Domain
              </Button>
            </Box>
          )}
          <AQDataGrid
            id="internal-account-domains"
            rows={domainsList.map((d) => ({
              id: d.id,
              domain: d.attributes.domain,
              created_at: d.attributes.created_at,
            }))}
            colDefs={[
              {
                field: 'domain',
                headerName: 'Domain',
                flex: 1,
                minWidth: 200,
                sortable: true,
                filterable: true,
              },
              {
                field: 'created_at',
                headerName: 'Added',
                width: 180,
                sortable: true,
                filterable: false,
                renderCell: (params: any) => (
                  <Typography variant="body2" color="text.secondary">
                    {formatDistanceToNow(parseJSON(params.value), {
                      addSuffix: true,
                    })}
                  </Typography>
                ),
              },
              {
                field: 'actions',
                headerName: '',
                width: 100,
                sortable: false,
                filterable: false,
                renderCell: (params: any) =>
                  canPerform(internalRole, 'manage_entitlements') ? (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => removeDomain.mutate(params.row.id)}
                      disabled={removeDomain.isPending}
                    >
                      Remove
                    </Button>
                  ) : null,
              },
            ]}
            loading={domainsQuery.isLoading}
            gridProps={{
              rowHeight: 52,
              sortingMode: 'client' as const,
              filterMode: 'client' as const,
              paginationMode: 'client' as const,
              disableColumnMenu: true,
              disableRowSelectionOnClick: true,
            }}
            customErrorElement={
              domainsQuery.isError ? (
                <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
                  Failed to load domains
                </Typography>
              ) : undefined
            }
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </>
      )}

      {/* Row actions menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuAnchor) {
              setRemoveTarget({
                userId: menuAnchor.user.id,
                displayName:
                  menuAnchor.user.attributes.display_name ||
                  menuAnchor.user.attributes.email,
              });
            }
            setMenuAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          Remove user
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      {roleTarget && (
        <ChangeAccountUserRoleDialog
          open={Boolean(roleTarget)}
          onClose={() => setRoleTarget(null)}
          accountId={accountId}
          userId={roleTarget.userId}
          displayName={roleTarget.displayName}
          currentRole={roleTarget.currentRole}
        />
      )}

      {removeTarget && (
        <RemoveAccountUserDialog
          open={Boolean(removeTarget)}
          onClose={() => setRemoveTarget(null)}
          accountId={accountId}
          userId={removeTarget.userId}
          displayName={removeTarget.displayName}
        />
      )}

      <AssumeRoleDialog
        open={assumeRoleOpen}
        onClose={() => setAssumeRoleOpen(false)}
        activeCount={activeRoles.length}
        preselectedAccount={account || null}
      />

      <AddEntitlementDialog
        open={addEntitlementOpen}
        onClose={() => setAddEntitlementOpen(false)}
        accountId={accountId}
      />

      <AddDomainDialog
        open={addDomainOpen}
        onClose={() => setAddDomainOpen(false)}
        accountId={accountId}
      />

      {account && (
        <DeactivateAccountDialog
          open={deactivateOpen}
          onClose={() => setDeactivateOpen(false)}
          accountId={accountId}
          accountName={account.attributes.title || account.attributes.name}
          isDeactivated={isDeactivated}
        />
      )}
    </Box>
  );
};

export default InternalAccountDetail;
